/**
 * Tint: coloured translucent liquid drawn as a multiplicative filter (stained glass, layers of
 * coloured fluid). Every look constant lives here.
 *
 * Colour model. Per unit thickness an tint of linear colour c transmits
 *
 *   T = mix(1, c, TINT_STRENGTH)
 *
 * A ray through a same-colour slab of `run` cells crosses two faces of that slab; each face applies
 * half the slab's absorption, so the pair multiplies the destination by the slab's transmittance:
 *
 *   'beer'    (Beer-Lambert): face factor T^(run / 2), slab = T^run (deep goes dark and drifts toward
 *             the dominant channel: deep yellow turns amber, as real dye does)
 *   'pigment' (saturating):   face factor sqrt(mix(1, c, 1 - (1 - TINT_STRENGTH)^run)), slab approaches
 *             the picked colour itself and never goes darker than it
 *
 * Different tints (and different slabs) are separate filters whose factors multiply, so yellow over
 * cyan reads green whichever is in front.
 */
import { SOLID_FACE_OFFSET } from '@/voxels/solidConstants'

export const TINT_VOXEL_TYPE = 'tint'

/**
 * Tint never overwrites: every stroke (swept `fill` verb, legacy scribble `fill` writer) writes empty
 * cells only, so a drop of tint on paper or sand leaves the cells under the brush intact, and a new
 * colour poured onto tint pools on top of it instead of replacing it. `type` is canonical (tint has no aliases).
 */
export function tintStrokeNeverOverwrites(type: string | undefined): boolean {
  return type === TINT_VOXEL_TYPE
}

/** Colour of an tint voxel without `color`. */
export const TINT_DEFAULT_COLOR = 0x2f6bff

/** Fraction of the way from clear (1) to the tint's colour that one cell of tint absorbs. */
export const TINT_STRENGTH = 0.55

/**
 * Depth model (see the header). 'pigment': a deep pool or a tall layer reads as the cup's own
 * colour rather than going dark, and colours seen through each other mix like printer tints
 * (blue through yellow is green). Under 'beer' a ray through two deep layers turns almost black.
 */
export const TINT_DEPTH_MODEL: 'beer' | 'pigment' = 'pigment'

/** Longest same-colour run (cells) a face record counts; 4 bits in w3. */
export const TINT_RUN_CAP = 15

/**
 * Inset of an tint face into its own cell (voxel units). Coincident faces of two different tints are
 * then two separate quads 2 x inset apart; a face never coincides with another face.
 */
export const TINT_FACE_INSET = 0.01

/**
 * Inset of a face whose outside neighbour is opaque (solid, sand, clay). The opaque face is
 * pushed `SOLID_FACE_OFFSET` (0.024) into the tint cell, so the tint face sits in front of it: the ray
 * that enters the tint and stops on the solid still crosses both faces of the slab (tint on paper
 * shows the slab's full colour, not half of it), and the two faces never z-fight.
 */
export const TINT_OPAQUE_FACE_INSET = SOLID_FACE_OFFSET + TINT_FACE_INSET

/**
 * Top-surface sheen: a second, additive pass over +y faces whose outside neighbour is open (air,
 * bounds, non-opaque non-tint). Highlight = TINT_SHEEN_BASE + TINT_SHEEN_FRESNEL * (1 - |n.v|)^5,
 * drawn after every tint filter (render order) in TINT_SHEEN_COLOR.
 */
export const TINT_SHEEN_ENABLED = true
export const TINT_SHEEN_BASE = 0.012
export const TINT_SHEEN_FRESNEL = 0.14
export const TINT_SHEEN_COLOR = 0xffffff

/**
 * Render order of the filter pass (the sheen pass draws at +1). Negative: tint draws before the
 * transmissive materials (glass, ice, diamond, all at 0). Those write depth and refract a copy of
 * the frame taken at their first draw, so tint drawn after them is hidden behind a glass wall;
 * drawn before, it is in the copy and shows through the glass (the layers in Spill's jar).
 */
export const TINT_RENDER_ORDER = -10

/** Words per tint record (16 bytes, the solid record width). */
export const TINT_STORAGE_WORDS = 4

/**
 * Neighbour classes stored per direction in w3 bits 8-19 (2 bits each, +x,-x,+y,-y,+z,-z):
 * 0 = same-colour tint (no face), 1 = open (air, outside bounds, non-opaque non-tint),
 * 2 = opaque, 3 = tint of another colour.
 */
export const TINT_NEIGHBOR_SAME = 0
export const TINT_NEIGHBOR_OPEN = 1
export const TINT_NEIGHBOR_OPAQUE = 2
export const TINT_NEIGHBOR_OTHER_TINT = 3
